import { DraftCalculatorInput, DraftVesselType, SailingCondition } from "../models/calculators.models";
import CalculatorNumberInput from "./CalculatorNumberInput";

interface StandaloneDraftCalculatorProps {
  draft: DraftCalculatorInput;
  normalizedStw: number | null | undefined;
  onChange: (draft: DraftCalculatorInput) => void;
}

const vesselTypes: { value: DraftVesselType; label: string }[] = [
  { value: "Capes (incl. Newcs)", label: "Capes (incl. Newcs)" },
  { value: "Handy", label: "Handy" },
  { value: "Panamax (incl. Baby Capes)", label: "Panamax (incl. Baby Capes)" },
  { value: "Supramax", label: "Supramax" },
];

// Recreates the source-backed draft inputs and leaves unsupported vessel coefficients visibly unresolved.
const StandaloneDraftCalculator = ({ draft, normalizedStw, onChange }: StandaloneDraftCalculatorProps) => {
  const updateNumber = (field: keyof DraftCalculatorInput, value: number | null) => {
    onChange({ ...draft, [field]: value ?? 0 });
  };

  const updateVesselType = (vesselType: DraftVesselType) => onChange({ ...draft, vesselType });
  const updateCondition = (condition: SailingCondition) => onChange({ ...draft, condition });

  return (
    <section data-test="standalone-draft-calculator" className="min-w-0 p-5 lg:border-r lg:border-gray-900">
      <h2 className="text-center text-xl font-bold text-gray-950 dark:text-white">Standalone Draft Cons</h2>

      <div className="mt-10 grid gap-6 sm:grid-cols-[1.5fr_0.7fr_0.7fr_1.25fr]">
        <fieldset>
          <legend className="mb-1 text-xs font-medium">Vessel Type for Draft Coef</legend>
          {vesselTypes.map(({ value, label }) => (
            <label key={value} className="flex items-center gap-2 py-0.5 text-[11px]">
              <input
                data-test={`draft-vessel-type-${value}`}
                type="radio"
                name="draft-vessel-type"
                checked={draft.vesselType === value}
                onChange={() => updateVesselType(value)}
              />
              {label}
            </label>
          ))}
        </fieldset>

        <fieldset>
          <legend className="mb-1 text-xs font-medium">B/L</legend>
          {(["Ballast", "Laden"] as SailingCondition[]).map((condition) => (
            <label key={condition} className="flex items-center gap-2 py-0.5 text-[11px] capitalize">
              <input
                data-test={`draft-condition-${condition}`}
                type="radio"
                name="draft-condition"
                checked={draft.condition === condition}
                onChange={() => updateCondition(condition)}
              />
              {condition}
            </label>
          ))}
        </fieldset>

        <CalculatorNumberInput
          label="Speed Exp"
          value={draft.speedExponent}
          dataTest="draft-speed-exponent"
          onChange={(value) => updateNumber("speedExponent", value)}
        />

        <div className="rounded-sm bg-[#F3E5A3] p-2">
          <CalculatorNumberInput
            label="Normalisation for Draft (m)"
            value={draft.normalizationDraft}
            dataTest="draft-normalization-draft"
            onChange={(value) => updateNumber("normalizationDraft", value)}
          />
        </div>
      </div>

      <h3 className="mb-5 mt-16 text-center text-base font-semibold">Actual Voyage Data</h3>
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
        <CalculatorNumberInput label="STW (kn)" value={draft.stw} dataTest="draft-stw" onChange={(value) => updateNumber("stw", value)} />
        <CalculatorNumberInput label="ME Cons (mT/d)" value={draft.meConsumption} dataTest="draft-me-consumption" onChange={(value) => updateNumber("meConsumption", value)} />
        <CalculatorNumberInput label="AE Cons mT/d" value={draft.aeConsumption} dataTest="draft-ae-consumption" onChange={(value) => updateNumber("aeConsumption", value)} />
        <CalculatorNumberInput label="Draft (m)" value={draft.draft} dataTest="draft-actual-draft" onChange={(value) => updateNumber("draft", value)} />
      </div>

      <div className="mt-14 text-right" data-test="normalized-stw-result">
        <div className="text-5xl font-semibold tabular-nums">{normalizedStw == null ? "—" : normalizedStw.toFixed(2)}</div>
        <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">Normalized STW</div>
      </div>

      {(draft.vesselType !== "Capes (incl. Newcs)" || draft.condition !== "Ballast") && (
        <p className="mt-3 text-right text-xs text-amber-700 dark:text-amber-300">
          The PBIP does not provide a draft coefficient for this vessel type and condition.
        </p>
      )}
    </section>
  );
};

export default StandaloneDraftCalculator;
